import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type {
    PatentAnalysisJob,
    PatentCandidate,
    PatentSearchCandidatesResponse,
    PatentResearchResumePayload,
} from '@/types/patSearch';

export type {
    PatSearchJobStatus,
    PatSearchSource,
    PatentAnalysisJob,
    PatentCandidate,
} from '@/types/patSearch';

// ─── Types ─────────────────────────────────────────────────────
export interface StructuredBriefing {
    problemaTecnico: string;
    solucaoProposta: string;
    diferenciais: string;
    aplicacoes: string;
}

export interface PatentResult {
    publicationNumber: string;
    title: string;
    applicant: string;
    date: string;
    abstract: string;
    classification?: string;
    source: 'Espacenet' | 'INPI';
    url?: string;
}

export interface AnalyzedPatent extends PatentResult {
    id: string;
    selected: boolean;
    riskLevel: 'high' | 'medium' | 'low';
    score: number;
    justificativa: string;
}

export interface TechBlock {
    id: string;
    name: string;
    description: string;
}

export interface SearchLevel {
    level: number;
    label: string;
    cql: string;
    inpi: string;
}

export interface IpcCode {
    code: string;
    justification: string;
}

export type ResearchSearchSource = 'both' | 'inpi' | 'espacenet';

export interface SearchStrategy {
    techBlocks?: TechBlock[];
    blocks?: {
        id: string;
        connector: "AND" | "OR";
        groups: {
            id: string;
            terms_pt: string[];
            terms_en: string[];
        }[];
    }[];
    searchLevels?: SearchLevel[];
    ipc_codes: (string | IpcCode)[];
    // Legacy fallback
    keywords_pt?: string[];
    keywords_en?: string[];
}

interface ResearchState {
    rawInput: string;
    inputMode: 'audio' | 'text' | 'files';
    transcription: string;
    searchSource: ResearchSearchSource;
    briefing: StructuredBriefing | null;
    strategy: SearchStrategy | null;
    cqlQuery: string;
    searchResults: {
        espacenet: PatentResult[];
        inpi: PatentResult[];
    };
    analyzedPatents: AnalyzedPatent[];
    researchSessionId: string | null;
    candidateResults: PatentCandidate[];
    patSearchWarnings: string[];
    patSearchSourceStatus: PatentSearchCandidatesResponse["sourceStatus"] | null;
    selectedCandidateIds: string[];
    analysisJob: PatentAnalysisJob | null;
}

interface ResearchContextType extends ResearchState {
    setRawInput: (input: string) => void;
    setInputMode: (mode: 'audio' | 'text' | 'files') => void;
    setTranscription: (text: string) => void;
    setSearchSource: (source: ResearchSearchSource) => void;
    setBriefing: (briefing: StructuredBriefing) => void;
    setStrategy: (strategy: SearchStrategy) => void;
    setCqlQuery: (query: string) => void;
    setSearchResults: (results: { espacenet: PatentResult[]; inpi: PatentResult[] }) => void;
    setAnalyzedPatents: (patents: AnalyzedPatent[]) => void;
    setResearchSessionId: (sessionId: string | null) => void;
    setCandidateResults: (
        results: PatentCandidate[],
        meta?: {
            warnings?: string[];
            sourceStatus?: PatentSearchCandidatesResponse["sourceStatus"] | null;
        }
    ) => void;
    setSelectedCandidateIds: (ids: string[]) => void;
    setAnalysisJob: (job: PatentAnalysisJob | null) => void;
    hydrateResearchSession: (payload: PatentResearchResumePayload) => void;
    trackJourneyStep: (stepKey: string, eventType: 'view' | 'complete') => void;
    getJourneyMetrics: () => Record<string, { views: number; completes: number; lastEventAt: string }>;
    resetResearch: () => void;
}

// ─── Initial State ─────────────────────────────────────────────
const createInitialState = (): ResearchState => ({
    rawInput: '',
    inputMode: 'text',
    transcription: '',
    searchSource: 'both',
    briefing: null,
    strategy: null,
    cqlQuery: '',
    searchResults: { espacenet: [], inpi: [] },
    analyzedPatents: [],
    researchSessionId: null,
    candidateResults: [],
    patSearchWarnings: [],
    patSearchSourceStatus: null,
    selectedCandidateIds: [],
    analysisJob: null,
});

// ─── Context ───────────────────────────────────────────────────
const ResearchContext = createContext<ResearchContextType | undefined>(undefined);

export function ResearchProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<ResearchState>(createInitialState);
    const journeyStorageKey = 'research_journey_metrics_v1';
    const clearPatSearchState = useCallback((currentState: ResearchState): ResearchState => ({
        ...currentState,
        candidateResults: [],
        patSearchWarnings: [],
        patSearchSourceStatus: null,
        selectedCandidateIds: [],
        analysisJob: null,
    }), []);

    const setRawInput = useCallback((rawInput: string) => setState(s => ({ ...s, rawInput })), []);
    const setInputMode = useCallback((inputMode: 'audio' | 'text' | 'files') => setState(s => ({ ...s, inputMode })), []);
    const setTranscription = useCallback((transcription: string) => setState(s => ({ ...s, transcription })), []);
    const setSearchSource = useCallback((searchSource: ResearchSearchSource) => setState(s => ({ ...s, searchSource })), []);
    const setBriefing = useCallback((briefing: StructuredBriefing) => setState(s => ({ ...s, briefing })), []);
    const setStrategy = useCallback((strategy: SearchStrategy) => setState(s => ({ ...s, strategy })), []);
    const setCqlQuery = useCallback((cqlQuery: string) => setState(s => ({ ...s, cqlQuery })), []);
    const setSearchResults = useCallback((searchResults: { espacenet: PatentResult[]; inpi: PatentResult[] }) =>
        setState(s => ({ ...s, searchResults })), []);
    const setAnalyzedPatents = useCallback((analyzedPatents: AnalyzedPatent[]) =>
        setState(s => ({ ...s, analyzedPatents })), []);
    const setResearchSessionId = useCallback((researchSessionId: string | null) =>
        setState(s => {
            if (s.researchSessionId === researchSessionId) {
                return s;
            }

            return {
                ...clearPatSearchState(s),
                researchSessionId,
            };
        }), [clearPatSearchState]);
    const setCandidateResults = useCallback((
        candidateResults: PatentCandidate[],
        meta?: {
            warnings?: string[];
            sourceStatus?: PatentSearchCandidatesResponse["sourceStatus"] | null;
        }
    ) =>
        setState(s => ({
            ...s,
            candidateResults,
            patSearchWarnings: meta?.warnings || [],
            patSearchSourceStatus: meta?.sourceStatus || null,
            selectedCandidateIds: [],
            analysisJob: null,
        })), []);
    const setSelectedCandidateIds = useCallback((selectedCandidateIds: string[]) =>
        setState(s => {
            const validIds = new Set(s.candidateResults.map(candidate => candidate.id));
            return {
                ...s,
                selectedCandidateIds: selectedCandidateIds.filter(id => validIds.has(id)),
            };
        }), []);
    const setAnalysisJob = useCallback((analysisJob: PatentAnalysisJob | null) =>
        setState(s => ({ ...s, analysisJob })), []);
    const hydrateResearchSession = useCallback((payload: PatentResearchResumePayload) =>
        setState(s => ({
            ...s,
            researchSessionId: payload.sessionId,
            rawInput: payload.rawInput || '',
            transcription: payload.transcription || '',
            inputMode: payload.inputMode || 'text',
            briefing: payload.briefing,
            strategy: payload.strategy as SearchStrategy | null,
            cqlQuery: payload.cqlQuery || '',
            searchSource: payload.searchSource,
            candidateResults: payload.candidateResults || [],
            patSearchWarnings: payload.warnings || [],
            patSearchSourceStatus: payload.sourceStatus || null,
            selectedCandidateIds: payload.selectedCandidateIds || [],
            analysisJob: payload.analysisJob,
        })), []);
    const getJourneyMetrics = useCallback(() => {
        try {
            const raw = localStorage.getItem(journeyStorageKey);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') return parsed;
            return {};
        } catch {
            return {};
        }
    }, []);
    const trackJourneyStep = useCallback((stepKey: string, eventType: 'view' | 'complete') => {
        if (!stepKey) return;
        const current = getJourneyMetrics();
        const item = current[stepKey] || { views: 0, completes: 0, lastEventAt: new Date().toISOString() };
        if (eventType === 'view') item.views += 1;
        if (eventType === 'complete') item.completes += 1;
        item.lastEventAt = new Date().toISOString();
        current[stepKey] = item;
        localStorage.setItem(journeyStorageKey, JSON.stringify(current));
    }, [getJourneyMetrics]);
    const resetResearch = useCallback(() => setState(createInitialState()), []);

    return (
        <ResearchContext.Provider value={{
            ...state,
            setRawInput, setInputMode, setTranscription, setSearchSource, setBriefing,
            setStrategy, setCqlQuery, setSearchResults, setAnalyzedPatents,
            setResearchSessionId, setCandidateResults, setSelectedCandidateIds, setAnalysisJob,
            hydrateResearchSession,
            trackJourneyStep, getJourneyMetrics, resetResearch
        }}>
            {children}
        </ResearchContext.Provider>
    );
}

export function useResearch(): ResearchContextType {
    const ctx = useContext(ResearchContext);
    if (!ctx) throw new Error('useResearch must be used within ResearchProvider');
    return ctx;
}
